import classNames from 'classnames';
import React from 'react';
import {startCase} from 'lodash';

import {Button, Dropdown, DropdownItem, Icon, Text} from '@albert-io/atomic';

export type Value = 'all' | 'closed' | 'open' | 'toDo';

interface DropdownOption {
  subtext?: string;
  value: Value;
}

interface Props extends PropsWithClassNameOptional {
  disabled: boolean;
  isScopedToClassroom: boolean;
  onValueChange: (value: Value) => void;
  value: Value;
}

export default function FilterDropdown({
  className,
  disabled,
  isScopedToClassroom,
  onValueChange,
  value: valueProp
}: Props) {
  'use memo';

  const options: DropdownOption[] = [
    {
      value: 'toDo'
    },
    {
      subtext: 'Assignments you can still submit, including past due.',
      value: 'open'
    },
    {
      value: 'closed'
    }
  ];

  if (isScopedToClassroom) {
    options.unshift({
      value: 'all'
    });
  }

  function renderDropdownChildren() {
    const toDoIndex = options.findIndex(({value}) => value === 'toDo');

    const children = options.map(({subtext, value}) => (
      <DropdownItem
        bold={false}
        className={classNames('items-center justify-start rounded-x1 px-x1-half py-x1', {
          'background-info-subtle': valueProp === value
        })}
        icon={valueProp === value ? 'check' : undefined}
        iconColor='info'
        key={value}
        onClick={() => {
          onValueChange(value);
        }}
        subtext={subtext}
      >
        <Text>{startCase(value)}</Text>
      </DropdownItem>
    ));

    children.splice(toDoIndex + 1, 0, <hr className='m-x1' key='todo-separator' />);

    return children;
  }

  return (
    <Dropdown
      className={className}
      closeOnItemClicked
      contentClassName='p-x1'
      trayClassName='w-full m:w-[275px]'
      positionFixed
      trayProps={{
        modifiers: {
          flip: {enabled: false},
          preventOverflow: {
            boundariesElement: 'viewport'
          }
        }
      }}
      trigger={
        <Button
          className='items-center gap-x1-quarter font-normal'
          color='secondary'
          disabled={disabled}
          variant='outlined'
        >
          {startCase(valueProp)}
          <Icon icon='caret-down' />
        </Button>
      }
    >
      {renderDropdownChildren()}
    </Dropdown>
  );
}
